import { useEffect, useMemo, useRef } from "react";
import { pointInPolygon } from "../lib/geometry";
import type { Point } from "../types";

export interface RenderedGrassRegion {
  id: string;
  polygons: Point[][];
  color: string;
  density: number;
  bladeSize: number;
  opacity: number;
  wind: number;
}

interface Props {
  regions: RenderedGrassRegion[];
  bounds: { left: number; top: number; width: number; height: number };
  pixelsPerGrid?: number;
  paused?: boolean;
  projectionMask?: string;
}

const VERTEX_SHADER = `#version 300 es
precision highp float;
in vec2 a_vertex;
in vec2 a_position;
in vec4 a_shape;
in vec4 a_color;
uniform vec2 u_resolution;
uniform float u_time;
uniform sampler2D u_wind_map;
out float v_progress;
out vec4 v_color;

void main() {
  float angle = a_shape.x;
  float lengthPx = a_shape.y;
  float widthPx = a_shape.z;
  float windAmount = a_shape.w;
  vec2 windUv = fract(a_position / max(u_resolution, vec2(1.0)) * 2.7 + vec2(u_time * 0.018, -u_time * 0.012));
  vec2 wind = texture(u_wind_map, windUv).rg * 2.0 - 1.0;
  float gust = sin(u_time * 1.15 + dot(a_position, vec2(0.021, 0.017))) * 0.45 + wind.r * 0.72;
  float bentAngle = angle + gust * windAmount * 0.34;
  vec2 direction = vec2(cos(bentAngle), sin(bentAngle));
  vec2 across = vec2(-direction.y, direction.x);
  float tip = a_vertex.y;
  vec2 local = across * a_vertex.x * widthPx + direction * tip * lengthPx;
  local += across * wind.g * windAmount * lengthPx * tip * tip * 0.22;
  vec2 pixel = a_position + local;
  vec2 clip = vec2(pixel.x / u_resolution.x * 2.0 - 1.0, 1.0 - pixel.y / u_resolution.y * 2.0);
  gl_Position = vec4(clip, 0.0, 1.0);
  v_progress = tip;
  v_color = a_color;
}`;

const FRAGMENT_SHADER = `#version 300 es
precision mediump float;
in float v_progress;
in vec4 v_color;
out vec4 outColor;
void main() {
  vec3 shaded = mix(v_color.rgb * 0.42, v_color.rgb * 1.18, smoothstep(0.0, 1.0, v_progress));
  float alpha = v_color.a * mix(0.72, 1.0, v_progress);
  outColor = vec4(shaded, alpha);
}`;

function compile(gl: WebGL2RenderingContext, type: number, source: string) {
  const shader = gl.createShader(type); if (!shader) throw new Error("Unable to allocate the grass shader.");
  gl.shaderSource(shader, source); gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) { const message = gl.getShaderInfoLog(shader) ?? "Grass shader failed to compile."; gl.deleteShader(shader); throw new Error(message); }
  return shader;
}

const hash = (x: number, y: number, seed: number) => {
  const value = Math.sin(x * 127.1 + y * 311.7 + seed * 74.7) * 43758.5453;
  return value - Math.floor(value);
};

function colorChannels(color: string) {
  const match = /^#([0-9a-f]{6})$/i.exec(color);
  const value = Number.parseInt(match?.[1] ?? "4f813d", 16);
  return [(value >> 16 & 255) / 255, (value >> 8 & 255) / 255, (value & 255) / 255];
}

export function grassInstances(regions: RenderedGrassRegion[], width: number, height: number, pixelsPerGrid = 25) {
  const maxInstances = width * height > 4_000_000 ? 40_000 : 52_000;
  const gridScale = Number.isFinite(pixelsPerGrid) ? Math.max(4, pixelsPerGrid) : 25;
  const prepared = regions.flatMap((region, regionIndex) => {
    const polygons = region.polygons.filter((polygon) => polygon.length >= 3); if (!polygons.length) return [];
    const points = polygons.flat(); const minX = Math.max(0, Math.min(...points.map((point) => point.x))); const maxX = Math.min(width, Math.max(...points.map((point) => point.x)));
    const minY = Math.max(0, Math.min(...points.map((point) => point.y))); const maxY = Math.min(height, Math.max(...points.map((point) => point.y)));
    const density = Math.max(0.08, Math.min(1, region.density));
    // Density belongs to the painted map area, not to the current projector's
    // CSS pixels. Keeping spacing in grid-square units prevents calibration
    // changes from multiplying the instance count until only the first strip
    // of a large grass region is rendered.
    const idealSpacing = Math.max(1.5, gridScale * (0.5 - density * 0.4));
    const estimatedInstances = Math.max(1, (maxX - minX) * (maxY - minY) / (idealSpacing * idealSpacing));
    return [{ region, regionIndex, polygons, minX, maxX, minY, maxY, idealSpacing, estimatedInstances }];
  });
  const estimatedTotal = prepared.reduce((sum, item) => sum + item.estimatedInstances, 0);
  // Increase spacing uniformly when necessary instead of stopping midway
  // through the map. This keeps every visible part of every grass region populated.
  const budgetScale = estimatedTotal > maxInstances * 0.92 ? Math.sqrt(estimatedTotal / (maxInstances * 0.92)) : 1;
  const values: number[] = []; let total = 0;
  for (const item of prepared) {
    const { region, regionIndex, polygons, minX, maxX, minY, maxY } = item; const spacing = item.idealSpacing * budgetScale; const [red, green, blue] = colorChannels(region.color);
    const sizeScale = Math.max(0.75, Math.min(3.2, gridScale / 25)); const baseSize = (5 + Math.max(0, Math.min(1, region.bladeSize)) * 12) * sizeScale;
    for (let y = minY; y <= maxY && total < maxInstances; y += spacing) for (let x = minX; x <= maxX && total < maxInstances; x += spacing) {
      const jitterX = (hash(x, y, regionIndex + 1) - 0.5) * spacing * 0.94; const jitterY = (hash(y, x, regionIndex + 9) - 0.5) * spacing * 0.94;
      const position = { x: x + jitterX, y: y + jitterY }; if (!polygons.some((polygon) => pointInPolygon(position, polygon))) continue;
      const random = hash(position.x, position.y, regionIndex + 17); const shade = 0.76 + hash(position.y, position.x, regionIndex + 31) * 0.42;
      values.push(position.x, position.y, random * Math.PI * 2, baseSize * (0.66 + random * 0.68), Math.max(1.1, baseSize * (0.12 + hash(position.x, position.y, 43) * 0.07)), Math.max(0, Math.min(1, region.wind)), Math.min(1, red * shade), Math.min(1, green * shade), Math.min(1, blue * shade), Math.max(0.08, Math.min(1, region.opacity)));
      total += 1;
    }
  }
  return new Float32Array(values);
}

function windTexture(gl: WebGL2RenderingContext) {
  const size = 64; const pixels = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y += 1) for (let x = 0; x < size; x += 1) {
    const index = (y * size + x) * 4; const broad = Math.sin(x * 0.19) * Math.cos(y * 0.14); const detail = hash(x, y, 73) * 2 - 1;
    pixels[index] = Math.round((broad * 0.34 + detail * 0.16 + 0.5) * 255); pixels[index + 1] = Math.round((Math.cos(x * 0.11 + y * 0.17) * 0.34 + hash(y, x, 91) * 0.16 + 0.42) * 255); pixels[index + 2] = 128; pixels[index + 3] = 255;
  }
  const texture = gl.createTexture(); gl.bindTexture(gl.TEXTURE_2D, texture); gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, size, size, 0, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
  return texture;
}

export function GrassRegions({ regions, bounds, pixelsPerGrid = 25, paused = false, projectionMask }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const validRegions = useMemo(() => regions.filter((region) => region.polygons.some((polygon) => polygon.length >= 3)), [regions]);
  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas || !validRegions.length || bounds.width <= 0 || bounds.height <= 0) return;
    canvas.dataset.grassSetupCount = String(Number(canvas.dataset.grassSetupCount ?? 0) + 1);
    const gl = canvas.getContext("webgl2", { alpha: true, antialias: true, premultipliedAlpha: true });
    if (!gl) { canvas.dataset.grassRenderer = "unavailable"; return; }
    let vertex: WebGLShader; let fragment: WebGLShader;
    try { vertex = compile(gl, gl.VERTEX_SHADER, VERTEX_SHADER); fragment = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER); }
    catch (error) { canvas.dataset.grassRenderer = "unavailable"; canvas.dataset.grassError = error instanceof Error ? error.message : "Grass shader failed."; return; }
    const program = gl.createProgram();
    if (!program) { gl.deleteShader(vertex); gl.deleteShader(fragment); return; }
    gl.attachShader(program, vertex); gl.attachShader(program, fragment); gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      canvas.dataset.grassRenderer = "unavailable"; canvas.dataset.grassError = gl.getProgramInfoLog(program) ?? "Grass shader failed to link.";
      gl.deleteProgram(program); gl.deleteShader(vertex); gl.deleteShader(fragment); return;
    }
    gl.useProgram(program); gl.enable(gl.BLEND); gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    const vertexBuffer = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer); gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-0.5, 0, 0.5, 0, 0, 1]), gl.STATIC_DRAW);
    const vertexLocation = gl.getAttribLocation(program, "a_vertex"); gl.enableVertexAttribArray(vertexLocation); gl.vertexAttribPointer(vertexLocation, 2, gl.FLOAT, false, 0, 0);
    const instanceData = grassInstances(validRegions, bounds.width, bounds.height, pixelsPerGrid); const instanceCount = instanceData.length / 10; const instanceBuffer = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, instanceBuffer); gl.bufferData(gl.ARRAY_BUFFER, instanceData, gl.STATIC_DRAW);
    const stride = 10 * 4; const attributes = [["a_position", 2, 0], ["a_shape", 4, 2], ["a_color", 4, 6]] as const;
    for (const [name, size, offset] of attributes) { const location = gl.getAttribLocation(program, name); gl.enableVertexAttribArray(location); gl.vertexAttribPointer(location, size, gl.FLOAT, false, stride, offset * 4); gl.vertexAttribDivisor(location, 1); }
    const texture = windTexture(gl); const windMap = gl.getUniformLocation(program, "u_wind_map"); gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, texture); gl.uniform1i(windMap, 0);
    const resolution = gl.getUniformLocation(program, "u_resolution"); const time = gl.getUniformLocation(program, "u_time"); const preferredDpr = Math.min(window.devicePixelRatio || 1, 1.5); const maxViewport = gl.getParameter(gl.MAX_VIEWPORT_DIMS) as Int32Array; const maxBufferSide = 2048; let frame = 0; let frameTimer: number | undefined;
    const animate = !paused && validRegions.some((region) => region.wind > 0.02);
    const schedule = () => {
      if (!animate) return;
      frameTimer = window.setTimeout(() => { frameTimer = undefined; frame = requestAnimationFrame(draw); }, 1000 / 20);
    };
    const draw = (now: number) => {
      const cssWidth = Math.max(1, canvas.clientWidth || bounds.width); const cssHeight = Math.max(1, canvas.clientHeight || bounds.height);
      // Tens of thousands of thin blades remain sharp at this resolution, while
      // allowing Retina/4K CSS pixels to create 4608px-wide grass buffers costs
      // tens of megabytes and destabilizes the other live WebGL layers.
      const renderScale = Math.max(0.1, Math.min(preferredDpr, maxBufferSide / cssWidth, maxBufferSide / cssHeight, maxViewport[0] / cssWidth, maxViewport[1] / cssHeight));
      const width = Math.max(1, Math.round(cssWidth * renderScale)); const height = Math.max(1, Math.round(cssHeight * renderScale));
      if (canvas.width !== width || canvas.height !== height) { canvas.width = width; canvas.height = height; }
      gl.viewport(0, 0, width, height); gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT); gl.uniform2f(resolution, bounds.width, bounds.height); gl.uniform1f(time, paused ? 2.4 : now / 1000); gl.drawArraysInstanced(gl.TRIANGLES, 0, 3, instanceCount);
      canvas.dataset.grassRenderer = "webgl2-instanced"; canvas.dataset.grassInstanceCount = String(instanceCount); canvas.dataset.grassRenderScale = renderScale.toFixed(3); canvas.dataset.renderedFrame = "true"; schedule();
    };
    draw(performance.now());
    return () => { cancelAnimationFrame(frame); if (frameTimer !== undefined) clearTimeout(frameTimer); gl.deleteTexture(texture); gl.deleteBuffer(vertexBuffer); gl.deleteBuffer(instanceBuffer); gl.deleteProgram(program); gl.deleteShader(vertex); gl.deleteShader(fragment); };
  }, [bounds.height, bounds.width, paused, pixelsPerGrid, validRegions]);
  if (!validRegions.length) return null;
  return <div className="grass-regions-layer" style={{ ...bounds, maskImage: projectionMask, WebkitMaskImage: projectionMask, maskSize: "100% 100%", WebkitMaskSize: "100% 100%" }} data-grass-region-count={validRegions.length} data-grass-animation={paused ? "paused" : "wind-map"} data-grass-view="top-down" data-grass-layer="above-map-only" data-grass-mask="unified-blades" data-grass-density-model="map-space" data-grass-grid-pixels={pixelsPerGrid} data-projection-mask={projectionMask ? "active" : "none"}><canvas ref={canvasRef} aria-hidden="true" /></div>;
}
